import React from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #08090c;
  --panel: #0e1015;
  --surface: #15181f;
  --text: #f4f6fa;
  --dim: #a1a7b3;
  --faint: #6b7280;
  --accent: #ccff00;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  line-height: 1.65;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 40px 22px 80px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 22px;
  margin-bottom: 28px;
  border-bottom: 1px solid var(--border);
`;

const Bolt = styled.div`
  width: 6px;
  height: 34px;
  border-radius: 3px;
  background: var(--accent);
`;

const Brand = styled.div`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: 0.5px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 30px;
  margin: 6px 0 4px;
`;

const Updated = styled.p`
  color: var(--faint);
  font-size: 14px;
  margin-bottom: 28px;
`;

const Section = styled.h2`
  font-size: 19px;
  margin: 34px 0 10px;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 16px;
  }

  strong {
    color: var(--text);
  }

  a {
    color: var(--accent);
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  ul {
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 18px 20px;
  margin: 16px 0;
`;

const Note = styled.p`
  font-size: 14px;
  color: var(--faint);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;

  td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--border);
    font-size: 15px;
    color: var(--dim);
    vertical-align: top;
  }

  td:first-child {
    color: var(--text);
    white-space: nowrap;
    width: 38%;
  }
`;

const Footer = styled.footer`
  margin-top: 48px;
  padding-top: 22px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 14px;
`;

const SportsNest = () => {
  return (
    <Page>
      <Wrap>
        <Header>
          <Bolt />
          <Brand>
            SPORTS<span>NEST</span>
          </Brand>
        </Header>

        <Title>Privacy Policy</Title>
        <Updated>Last updated: 9 June 2026</Updated>

        <Body>
          <p>
            SportsNest (“we”, “us”, “the app”) respects your privacy. This policy
            explains what information the app collects, how it is used, and your
            choices. You can use SportsNest{" "}
            <strong>without creating an account</strong>.
          </p>

          <Section as="h2">Information we collect</Section>
          <ul>
            <li>
              <strong>Account information (optional).</strong> If you choose to
              sign in, we collect your <strong>email address</strong> — and, for
              Google Sign-In, your name and profile photo — through Google
              Firebase Authentication. Sign-in is optional and is used only to
              sync your preferences across devices.
            </li>
            <li>
              <strong>Approximate location.</strong> At launch the app makes a
              one-time lookup of your <strong>IP address</strong> (via ipwho.is)
              to detect your country and show channels from your region. We do
              not use GPS/precise location and do not store your IP address.
            </li>
            <li>
              <strong>Advertising identifier.</strong> Ads are provided by{" "}
              <strong>Google AdMob</strong>, which may use your device’s
              advertising ID to deliver and measure ads. In the EEA/UK we request
              your consent first via Google’s User Messaging Platform.
            </li>
            <li>
              <strong>App preferences &amp; activity.</strong> Your favorites,
              watch history, region and theme are stored on your device. If you
              sign in, these are also synced to your Firebase profile.
            </li>
          </ul>

          <Section as="h2">How we use your information</Section>
          <p>
            To provide live channels, remember your preferences, sync them across
            devices when you’re signed in, deliver and measure ads that keep the
            app free, and detect your region so the right channels appear by
            default.
          </p>

          <Section as="h2">Sharing &amp; third parties</Section>
          <p>
            We do not sell your personal data. Your data is processed by these
            services:
          </p>
          <Table>
            <tbody>
              <tr>
                <td>Google Firebase</td>
                <td>Authentication &amp; preference sync (only if you sign in)</td>
              </tr>
              <tr>
                <td>Google AdMob</td>
                <td>Advertising and measurement</td>
              </tr>
              <tr>
                <td>ipwho.is</td>
                <td>Approximate country detection from IP</td>
              </tr>
            </tbody>
          </Table>
          <Note>
            Channel streams are provided by third-party sources. Opening a channel
            connects your device directly to that source, which may have its own
            privacy practices. We do not host content.
          </Note>

          <Section as="h2">Data retention &amp; deletion</Section>
          <p>
            Locally stored data is removed when you uninstall the app or clear its
            data. If you signed in, you can request deletion of your synced
            profile at any time by emailing us at the address below.
          </p>

          <Section as="h2">Children</Section>
          <p>
            SportsNest is not directed to children under 13 and we do not
            knowingly collect data from them.
          </p>

          <Section as="h2">Security</Section>
          <p>
            Data transmitted by the app is encrypted in transit using HTTPS.
            Sign-in is handled by Google Firebase.
          </p>

          <Section as="h2">Changes to this policy</Section>
          <p>
            We may update this policy from time to time. Material changes will be
            reflected by the “Last updated” date above.
          </p>

          <Section as="h2">Contact us</Section>
          <Card>
            Questions or data-deletion requests:
            <br />
            <a href="mailto:hello@nimions.com">hello@nimions.com</a>
            <br />
            <a href="tel:+8801764928732">+880 1764 928732</a>
          </Card>
        </Body>

        <Footer>
          © 2026 SportsNest. This policy applies to the SportsNest mobile
          application (package <strong>com.sportsnest.app</strong>).
        </Footer>
      </Wrap>
    </Page>
  );
};

export default SportsNest;
